import type { ColorSystem } from '@mui/material/styles';
import type { SettingsState } from 'src/components/settings';
import type { ThemeOptions, ThemeColorScheme } from '../types';

import {
  setFont,
  hexToRgbChannel,
  createPaletteChannel,
} from 'minimal-shared/utils';

import {
  primaryColorPresets,
  preset6Light,
  preset6Dark,
} from './color-presets';
import { createShadowColor } from '../core/custom-shadows';
import { themeConfig } from '../theme-config';

// ----------------------------------------------------------------------

/**
 * Updates the core theme with the provided settings state.
 * @param theme - The base theme options to update.
 * @param settingsState - The settings state containing direction, fontFamily, contrast, and primaryColor.
 * @returns Updated theme options with applied settings.
 */

export function applySettingsToTheme(
  theme: ThemeOptions,
  settingsState?: SettingsState
): ThemeOptions {
  const {
    direction,
    fontFamily,
    contrast = 'default',
    primaryColor = 'default',
  } = settingsState ?? {};

  const isDefaultContrast = contrast === 'default';
  const isDefaultPrimaryColor = primaryColor === 'default';

  const lightPalette = theme.colorSchemes?.light
    ?.palette as ColorSystem['palette'];

  const primaryColorPalette = createPaletteChannel(
    primaryColorPresets[primaryColor]
  );
  // const secondaryColorPalette = createPaletteChannel(secondaryColorPresets[primaryColor]);

  const updateColorScheme = (schemeName: ThemeColorScheme) => {
    const currentScheme = theme.colorSchemes?.[schemeName];

    // Use mode-specific colors for preset6
    const getPrimaryPalette = () => {
      if (primaryColor === 'preset6') {
        return createPaletteChannel(
          schemeName === 'dark' ? preset6Dark : preset6Light
        );
      }
      return primaryColorPalette;
    };

    const schemePrimaryPalette = getPrimaryPalette();

    const updatedPalette = {
      ...currentScheme?.palette,
      ...(!isDefaultPrimaryColor && {
        primary: schemePrimaryPalette,
        // secondary: secondaryColorPalette,
      }),
      ...(schemeName === 'light' && {
        background: {
          ...lightPalette?.background,
          ...(!isDefaultContrast && {
            default: lightPalette.grey[300],
            defaultChannel: hexToRgbChannel(lightPalette.grey[300]),
          }),
        },
      }),
    };

    const updatedCustomShadows = {
      ...currentScheme?.customShadows,
      ...(!isDefaultPrimaryColor && {
        primary: createShadowColor(schemePrimaryPalette.mainChannel),
        // secondary: createShadowColor(secondaryColorPalette.mainChannel),
      }),
    };

    return {
      ...(currentScheme || {}),
      palette: updatedPalette,
      customShadows: updatedCustomShadows,
    };
  };

  return {
    ...theme,
    direction,
    colorSchemes: {
      light: updateColorScheme('light'),
      dark: updateColorScheme('dark'),
    },
    typography: {
      ...theme.typography,
      fontFamily: setFont(themeConfig.fontFamily.primary),
    },
  };
}
